<template>
  <!--
    StepSkyldner.vue - Step 2: Skyldner (NY VERSION)
    ================================================
    Skyldner-step med AI pre-fill og matching til eksisterende skyldnere.

    NY FUNKTIONALITET:
    - AI-udtrukket skyldner-info vises som forslag
    - Match mod eksisterende skyldnere (CVR/CPR først, derefter navn)
    - Bruger verificerer og godkender data

    Features:
    - AI pre-fill banner med match-info
    - Eksisterende skyldner dropdown
    - CVR-opslag med auto-udfyld
    - Postnummer-opslag for bynavn
  -->
  <div class="step-skyldner">
    <!-- Overskrift -->
    <div class="step-header mb-2">
      <h4>{{ $t('DEBTIA_DEBTOR') }}</h4>
      <p class="text-muted mb-0">
        {{ $t('DEBTIA_CREATE_DEBTOR_SUBTITLE') }}
      </p>
    </div>

    <!-- AI Match Banner (vis hvis der er AI-udtrukket eller matchet debtor) -->
    <div
      v-if="aiExtractedDebtor || matchedExistingDebtor"
      class="ai-match-banner mb-2"
    >
      <div class="match-header">
        <feather-icon
          icon="CpuIcon"
          size="20"
          class="text-primary mr-50"
        />
        <span class="font-weight-bold">{{ $t('DEBTIA_CREATE_DEBTOR_AI_SUGGESTION') }}</span>
      </div>

      <!-- Match fundet -->
      <div
        v-if="matchedExistingDebtor"
        class="match-content match-found"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="16"
          class="text-success mr-50"
        />
        <span>
          {{ $t('DEBTIA_CREATE_DEBTOR_MATCH_FOUND') }}
          <strong>{{ matchedExistingDebtor.name }}</strong>
          <span
            v-if="matchedExistingDebtor.matchType === 'cvr'"
            class="text-muted"
          >
            (CVR: {{ matchedExistingDebtor.cvr }})
          </span>
          <span
            v-else-if="matchedExistingDebtor.matchType === 'cpr'"
            class="text-muted"
          >
            ({{ $t('DEBTIA_CREATE_DEBTOR_CPR_MATCH') }})
          </span>
        </span>
        <b-button
          variant="success"
          size="sm"
          class="ml-auto"
          @click="useMatchedDebtor"
        >
          <feather-icon
            icon="CheckIcon"
            class="mr-25"
          />
          {{ $t('DEBTIA_CREATE_DEBTOR_USE_THIS') }}
        </b-button>
      </div>

      <!-- Kun AI-udtrukket (ingen match) -->
      <div
        v-else-if="aiExtractedDebtor"
        class="match-content match-new"
      >
        <feather-icon
          icon="UserPlusIcon"
          size="16"
          class="text-info mr-50"
        />
        <span>
          {{ $t('DEBTIA_CREATE_DEBTOR_NEW') }}
          <strong>{{ aiExtractedDebtor.companyName || aiExtractedDebtor.personName || $t('DEBTIA_COMMON_UNNAMED') }}</strong>
          <span
            v-if="aiExtractedDebtor.cvr"
            class="text-muted"
          >
            (CVR: {{ aiExtractedDebtor.cvr }})
          </span>
        </span>
        <b-button
          variant="primary"
          size="sm"
          class="ml-auto"
          @click="useAiExtractedDebtor"
        >
          <feather-icon
            icon="CheckIcon"
            class="mr-25"
          />
          {{ $t('DEBTIA_CREATE_DEBTOR_USE_DATA') }}
        </b-button>
      </div>
    </div>

    <!-- Skyldner-liste -->
    <div
      v-for="(debtor, index) in debtors"
      :key="debtor.id"
      class="debtor-card"
    >
      <b-card>
        <!-- Skyldner header -->
        <div class="debtor-header d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">
            <feather-icon
              icon="UserIcon"
              class="mr-50"
            />
            {{ $t('DEBTIA_CREATE_DEBTOR_N', { n: index + 1 }) }}
            <b-badge
              v-if="debtor.isExisting"
              variant="light-success"
              class="ml-50"
            >
              {{ $t('DEBTIA_COMMON_EXISTING') }}
            </b-badge>
            <b-badge
              v-if="debtor.aiPrefilled"
              variant="light-primary"
              class="ml-50"
            >
              <feather-icon
                icon="CpuIcon"
                size="10"
                class="mr-25"
              />
              {{ $t('DEBTIA_CREATE_DEBTOR_AI') }}
            </b-badge>
            <b-badge
              v-if="debtor.isValid"
              variant="light-success"
              class="ml-50"
            >
              <feather-icon
                icon="CheckIcon"
                size="12"
              />
            </b-badge>
          </h5>
          <b-button
            v-if="debtors.length > 1"
            variant="flat-danger"
            size="sm"
            @click="$emit('remove-debtor', debtor.id)"
          >
            <feather-icon
              icon="TrashIcon"
              class="mr-25"
            />
            {{ $t('DEBTIA_COMMON_REMOVE') }}
          </b-button>
        </div>

        <!-- Eksisterende skyldner dropdown -->
        <b-row v-if="existingDebtors.length > 0">
          <b-col cols="12">
            <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_SELECT_EXISTING')">
              <v-select
                :value="debtor.debtorId"
                :options="existingDebtors"
                :reduce="d => d.value"
                label="label"
                :placeholder="$t('DEBTIA_CREATE_DEBTOR_SEARCH_EXISTING')"
                :clearable="true"
                class="existing-debtor-select"
                @input="handleExistingDebtorSelect(debtor.id, $event)"
              />
              <small class="text-muted">
                {{ $t('DEBTIA_CREATE_DEBTOR_SELECT_OR_MANUAL') }}
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <hr
          v-if="existingDebtors.length > 0"
          class="my-2"
        >

        <!-- Skyldnertype - kun vis hvis AI IKKE har sat skyldnertype, eller hvis bruger vil ændre -->
        <b-row v-if="!debtor.debtorType || showDebtorTypeSelector[debtor.id]">
          <b-col cols="12">
            <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_TYPE_LABEL')">
              <div class="debtor-type-selector">
                <div
                  v-for="type in debtorTypes"
                  :key="type.value"
                  class="debtor-type-option"
                  :class="{ selected: debtor.debtorType === type.value }"
                  @click="handleDebtorTypeChange(debtor.id, type.value)"
                >
                  <div class="option-content">
                    <feather-icon
                      :icon="getDebtorTypeIcon(type.value)"
                      size="28"
                      class="mb-75"
                    />
                    <div class="option-label">
                      {{ type.labelDa }}
                    </div>
                    <div class="option-hint">
                      {{ getDebtorTypeHint(type.value) }}
                    </div>
                  </div>
                  <div
                    v-if="debtor.debtorType === type.value"
                    class="option-check"
                  >
                    <feather-icon
                      icon="CheckCircleIcon"
                      size="20"
                    />
                  </div>
                </div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Vis valgt skyldnertype med mulighed for at ændre -->
        <b-row v-else-if="debtor.debtorType && !showDebtorTypeSelector[debtor.id]">
          <b-col cols="12">
            <div class="selected-debtor-type mb-2">
              <div class="d-flex align-items-center">
                <feather-icon
                  :icon="getDebtorTypeIcon(debtor.debtorType)"
                  size="18"
                  class="mr-50 text-primary"
                />
                <span class="font-weight-medium">{{ getDebtorTypeLabelDa(debtor.debtorType) }}</span>
                <b-button
                  variant="flat-primary"
                  size="sm"
                  class="ml-1 p-25"
                  @click="toggleDebtorTypeSelector(debtor.id)"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="14"
                  />
                  {{ $t('DEBTIA_CREATE_DEBTOR_CHANGE') }}
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Resten af skyldner-formularen (genbrugt fra StepDebtor) -->
        <template v-if="debtor.debtorType">
          <!-- CVR/CPR -->
          <b-row>
            <b-col
              v-if="debtor.debtorType === 'person'"
              cols="12"
              md="6"
            >
              <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_CPR_LABEL')">
                <b-form-input
                  :value="debtor.cpr"
                  placeholder="XXXXXX-XXXX"
                  @input="$emit('update-debtor', debtor.id, 'cpr', $event)"
                  @blur="handleCprBlur(debtor, debtor.cpr)"
                />
                <small
                  v-if="cvrMessage[debtor.id]"
                  :class="cvrMessageClass(debtor.id)"
                >
                  {{ cvrMessage[debtor.id] }}
                </small>
                <small
                  v-else
                  class="text-muted"
                >{{ $t('DEBTIA_CREATE_DEBTOR_OPTIONAL') }}</small>
              </b-form-group>
            </b-col>

            <b-col
              v-else
              cols="12"
              md="6"
            >
              <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_CVR_LABEL')">
                <b-input-group>
                  <b-form-input
                    :value="debtor.cvr"
                    placeholder="12345678"
                    maxlength="8"
                    :state="cvrValidationState(debtor)"
                    @input="$emit('update-debtor', debtor.id, 'cvr', $event)"
                    @blur="handleCvrBlur(debtor)"
                  />
                  <b-input-group-append>
                    <b-button
                      variant="outline-primary"
                      :disabled="!debtor.cvr || debtor.cvr.length !== 8 || cvrLoading[debtor.id]"
                      @click="lookupCvr(debtor)"
                    >
                      <b-spinner
                        v-if="cvrLoading[debtor.id]"
                        small
                      />
                      <feather-icon
                        v-else
                        icon="SearchIcon"
                      />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
                <small
                  v-if="debtor.cvr && debtor.cvr.length !== 8"
                  class="text-danger"
                >
                  {{ $t('DEBTIA_CREATE_DEBTOR_CVR_8DIGITS') }}
                </small>
                <small
                  v-if="cvrMessage[debtor.id]"
                  :class="cvrMessageClass(debtor.id)"
                >
                  {{ cvrMessage[debtor.id] }}
                </small>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Navn og C/O -->
          <b-row>
            <b-col
              cols="12"
              md="8"
            >
              <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_NAME')">
                <b-form-input
                  :value="debtor.name"
                  :placeholder="$t('DEBTIA_CREATE_DEBTOR_NAME_PH')"
                  :state="debtor.name ? true : null"
                  @input="$emit('update-debtor', debtor.id, 'name', $event)"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group :label="$t('DEBTIA_CASES_CO')">
                <b-form-input
                  :value="debtor.co"
                  :placeholder="$t('DEBTIA_CREATE_DEBTOR_CO_PH')"
                  @input="$emit('update-debtor', debtor.id, 'co', $event)"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Adresse -->
          <h6 class="mt-2 mb-1">
            <feather-icon
              icon="MapPinIcon"
              class="mr-50"
            />
            {{ $t('DEBTIA_ADDRESS') }}
          </h6>

          <b-row>
            <b-col
              cols="12"
              md="8"
            >
              <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_STREET')">
                <b-form-input
                  :value="debtor.address.roadName"
                  :placeholder="$t('DEBTIA_CREATE_DEBTOR_STREET_PH')"
                  :state="debtor.address.roadName ? true : null"
                  @input="$emit('update-debtor', debtor.id, 'address.roadName', $event)"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_HOUSE')">
                <b-form-input
                  :value="debtor.address.houseNumber"
                  :placeholder="$t('DEBTIA_CREATE_DEBTOR_HOUSE_PH')"
                  :state="debtor.address.houseNumber ? true : null"
                  @input="$emit('update-debtor', debtor.id, 'address.houseNumber', $event)"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_ZIP')">
                <b-form-input
                  :value="debtor.address.zipCode"
                  placeholder="1234"
                  :state="debtor.address.zipCode ? true : null"
                  @input="handleZipCodeChange(debtor, $event)"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_CITY')">
                <b-form-input
                  :value="debtor.address.city"
                  :placeholder="$t('DEBTIA_CREATE_DEBTOR_CITY_PH')"
                  :state="debtor.address.city ? true : null"
                  :disabled="cityLoading[debtor.id]"
                  @input="$emit('update-debtor', debtor.id, 'address.city', $event)"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_PLACE')">
                <b-form-input
                  :value="debtor.address.place"
                  :placeholder="$t('DEBTIA_CREATE_DEBTOR_OPTIONAL')"
                  @input="$emit('update-debtor', debtor.id, 'address.place', $event)"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Kontakt (valgfrit) -->
          <h6 class="mt-2 mb-1">
            <feather-icon
              icon="PhoneIcon"
              class="mr-50"
            />
            {{ $t('DEBTIA_CREATE_DEBTOR_CONTACT') }} <small class="text-muted">({{ $t('DEBTIA_CREATE_DEBTOR_OPTIONAL') }})</small>
          </h6>

          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group :label="$t('DEBTIA_PHONE')">
                <b-form-input
                  :value="debtor.phone"
                  placeholder="+45 12 34 56 78"
                  @input="$emit('update-debtor', debtor.id, 'phone', $event)"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group :label="$t('DEBTIA_EMAIL')">
                <b-form-input
                  :value="debtor.email"
                  type="email"
                  placeholder="email@example.dk"
                  @input="$emit('update-debtor', debtor.id, 'email', $event)"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Bankoplysninger (valgfrit, collapsed) -->
          <b-link
            v-b-toggle="`bank-info-${debtor.id}`"
            class="d-flex align-items-center text-muted mt-2"
          >
            <feather-icon
              icon="ChevronDownIcon"
              class="mr-50"
            />
            {{ $t('DEBTIA_CREATE_DEBTOR_BANK') }}
          </b-link>

          <b-collapse :id="`bank-info-${debtor.id}`">
            <b-row class="mt-1">
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_BANK_REG')">
                  <b-form-input
                    :value="debtor.bankBranchNo"
                    placeholder="1234"
                    @input="$emit('update-debtor', debtor.id, 'bankBranchNo', $event)"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group :label="$t('DEBTIA_CREATE_DEBTOR_BANK_ACCOUNT')">
                  <b-form-input
                    :value="debtor.bankAccountNo"
                    placeholder="1234567890"
                    @input="$emit('update-debtor', debtor.id, 'bankAccountNo', $event)"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group label="CPN">
                  <b-form-input
                    :value="debtor.cpn"
                    @input="$emit('update-debtor', debtor.id, 'cpn', $event)"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-collapse>
        </template>

        <!-- Besked hvis type ikke er valgt -->
        <b-alert
          v-else
          variant="info"
          show
          class="mb-0"
        >
          <div class="alert-body">
            <feather-icon
              icon="InfoIcon"
              class="mr-50"
            />
            {{ $t('DEBTIA_CREATE_DEBTOR_SELECT_TYPE') }}
          </div>
        </b-alert>
      </b-card>
    </div>

    <!-- Valideringsfejl -->
    <div
      v-if="step2ValidationErrors.length > 0"
      class="validation-alert mt-1 mb-2"
    >
      <div class="d-flex align-items-start">
        <feather-icon
          icon="AlertTriangleIcon"
          size="20"
          class="mr-75 mt-25 text-warning flex-shrink-0"
        />
        <div>
          <strong>{{ $t('DEBTIA_COMMON_TO_CONTINUE') }}</strong>
          <ul class="mb-0 mt-50 pl-1">
            <li
              v-for="(error, index) in step2ValidationErrors"
              :key="index"
            >
              {{ error }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Tilføj skyldner knap -->
    <b-button
      variant="outline-primary"
      block
      @click="$emit('add-debtor')"
    >
      <feather-icon
        icon="PlusIcon"
        class="mr-50"
      />
      {{ $t('DEBTIA_CREATE_DEBTOR_ADD_ANOTHER') }}
    </b-button>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BBadge,
  BSpinner,
  BAlert,
  BCollapse,
  BLink,
  VBToggle,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  reactive,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  set as vueSet,
} from '@vue/composition-api'
import axios from '@axios'
import constants from '@/app-configs/constants'
import { useCaseCreation } from '../composables/useCaseCreation'
import { lookupCvr as cvrLookup } from '../services/cvrService'

export default {
  name: 'StepSkyldner',

  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BBadge,
    BSpinner,
    BAlert,
    BCollapse,
    BLink,
    vSelect,
  },

  directives: {
    'b-toggle': VBToggle,
  },

  props: {
    debtors: {
      type: Array,
      required: true,
    },
    debtorTypes: {
      type: Array,
      required: true,
    },
    clientId: {
      type: [String, Number],
      default: null,
    },
  },

  setup(props, { emit, root }) {
    const { state, setMatchedExistingDebtor, step2ValidationErrors } = useCaseCreation()

    // Loading states
    const cvrLoading = reactive({})
    const cityLoading = reactive({})
    const cvrMessage = reactive({})
    const cvrMessageSuccess = reactive({})

    // UI state for showing/hiding debtor type selector
    const showDebtorTypeSelector = reactive({})

    // Eksisterende skyldnere
    const existingDebtors = ref([])

    // CVR/CPR maps til hurtig matching mod eksisterende skyldnere
    const debtorCvrMap = reactive({}) // cvr -> { debtorId, name }
    const debtorCprMap = reactive({}) // cpr -> { debtorId, name }

    // AbortController til at annullere baggrunds-requests ved navigation
    const backgroundAbortController = new AbortController()

    // Lofter for baggrundsopslag mod /debtor/view.
    //
    // API'et har intet endpoint til at slå op på CVR/CPR, så klienten er nødt
    // til at hente skyldnere enkeltvis for at bygge et opslagskort. Uden loft
    // svarer det til ét request per skyldner i databasen, og browseren giver
    // op med ERR_INSUFFICIENT_RESOURCES. Navnematch i dropdownen klarer det
    // typiske tilfælde med ét enkelt kald.
    const MAX_BACKGROUND_DEBTOR_LOOKUPS = 60
    const BACKGROUND_LOOKUP_CONCURRENCY = 3

    // AI data
    const aiExtractedDebtor = computed(() => state.aiExtractedDebtor)
    const matchedExistingDebtor = computed(() => state.matchedExistingDebtor)

    // ============================================================
    // HELPER FUNCTIONS (defined first to avoid use-before-define)
    // ============================================================

    /**
     * Henter detaljer for en eksisterende skyldner og tilføjer til CVR/CPR maps.
     */
    async function fetchDebtorDetails(debtorId, signal) {
      try {
        const response = await axios.get(`/debtor/view?id=${debtorId}`, {
          signal: signal || backgroundAbortController.signal,
        })
        if (response.status === 200 && response.data.data) {
          const d = response.data.data
          if (d.cvr_number) {
            debtorCvrMap[d.cvr_number] = { debtorId: d.id, name: d.name }
          }
          if (d.cpr) {
            debtorCprMap[d.cpr] = { debtorId: d.id, name: d.name }
          }
        }
      } catch {
        // Ignore (inkl. abort)
      }
    }

    /**
     * Finder eksisterende skyldner via CVR i lokal map.
     */
    function findDebtorByCvrLocal(cvr) {
      if (!cvr) return null
      const clean = cvr.replace(/\s/g, '')
      return debtorCvrMap[clean] || null
    }

    /**
     * Finder eksisterende skyldner via CPR i lokal map.
     */
    function findDebtorByCprLocal(cpr) {
      if (!cpr) return null
      const clean = cpr.replace(/[-\s]/g, '')
      // Check both with and without dash
      return debtorCprMap[clean] || debtorCprMap[cpr] || null
    }

    function findDebtorByName(name) {
      const normalizedName = name.toLowerCase().trim()
      return existingDebtors.value.find(d => d.label.toLowerCase().includes(normalizedName)
        || normalizedName.includes(d.label.toLowerCase()))
    }

    /**
     * Normaliserer tekst til sammenligning: små bogstaver, ensartet
     * tegnsætning og mellemrum. "Tværvej 10 D, 1, 6" og "tværvej 10 d 1 6"
     * skal regnes som ens.
     */
    function normalizeForCompare(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[.,/\\-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    }

    /**
     * Sammenligner adresse mellem AI-data og en gemt skyldner.
     *
     * Kræver at både postnummer og vejnavn findes på begge sider og er ens.
     * Husnummer sammenlignes kun, når begge sider har det — gamle skyldnere
     * er ofte oprettet uden.
     */
    function addressesMatch(ai, stored) {
      const addr = stored.debtor_address || {}

      const aiZip = normalizeForCompare(ai.zipCode)
      const storedZip = normalizeForCompare(addr.zip_code)
      if (!aiZip || !storedZip || aiZip !== storedZip) return false

      const aiRoad = normalizeForCompare(ai.address)
      const storedRoad = normalizeForCompare(addr.road_name)
      if (!aiRoad || !storedRoad || aiRoad !== storedRoad) return false

      const aiHouse = normalizeForCompare(ai.houseNumber)
      const storedHouse = normalizeForCompare(addr.house_no)
      if (aiHouse && storedHouse && aiHouse !== storedHouse) return false

      return true
    }

    /**
     * Afgør om en gemt skyldner er den samme som den, AI har udtrukket.
     *
     * Reglerne er forskellige for personer og virksomheder, fordi risikoen er
     * det:
     *
     * - CPR og CVR er entydige. Stemmer de, er det et match. Er de
     *   modstridende, er det afvist uanset hvor ens alt andet ser ud.
     * - For en PRIVATPERSON er navn alene aldrig nok. "Lars Nielsen" er ikke
     *   en identifikation. Uden CPR kræves navn OG adresse.
     * - For en VIRKSOMHED er et firmanavn langt mere særpræget, og
     *   navnematch alene accepteres — men markeres som svagere match, så
     *   sagsbehandleren kan se, at det ikke er verificeret på CVR.
     *
     * Returnerer null hvis der ikke er match.
     */
    function evaluateDebtorMatch(ai, stored) {
      const aiCvr = (ai.cvr || '').replace(/\s/g, '')
      const aiCpr = (ai.cpr || '').replace(/[-\s]/g, '')
      const storedCvr = (stored.cvr_number || '').replace(/\s/g, '')
      const storedCpr = (stored.cpr || '').replace(/[-\s]/g, '')

      // Modstridende entydige numre udelukker match.
      if (aiCvr && storedCvr && aiCvr !== storedCvr) return null
      if (aiCpr && storedCpr && aiCpr !== storedCpr) return null

      if (aiCpr && storedCpr === aiCpr) return { matchType: 'cpr', strength: 'strong' }
      if (aiCvr && storedCvr === aiCvr) return { matchType: 'cvr', strength: 'strong' }

      // Uden entydigt nummer at gå efter afgør typen, hvad der kræves.
      const isPerson = stored.debtor_type === 'person' || (!aiCvr && Boolean(ai.personName))
      const hasAddressMatch = addressesMatch(ai, stored)

      if (hasAddressMatch) return { matchType: 'name-address', strength: 'strong' }

      // Her stopper personer. Et navnesammenfald er ikke en identifikation,
      // og en forkert skyldner på en inkassosag er dyrere end at bede
      // brugeren vælge selv.
      if (isPerson) return null

      return { matchType: 'name', strength: 'weak' }
    }

    /**
     * Hurtig AI-matching mod eksisterende skyldnere.
     * Bruger navn-match i dropdown (instant) + bekræfter CVR/CPR med ét API-kald.
     * Returnerer matched debtor ID eller null.
     */
    async function quickMatchAiDebtor() {
      const aiDebtor = state.aiExtractedDebtor
      if (!aiDebtor) return null

      const aiName = (aiDebtor.companyName || aiDebtor.personName || '').toLowerCase().trim()
      const aiCvr = aiDebtor.cvr ? aiDebtor.cvr.replace(/\s/g, '') : ''
      const aiCpr = aiDebtor.cpr ? aiDebtor.cpr.replace(/[-\s]/g, '') : ''

      if (!aiCvr && !aiCpr && !aiName) return null

      // Strategi 1: Navn-match i dropdown (instant, 0 API-kald)
      // Derefter bekræft CVR/CPR med ét /debtor/view kald
      if (aiName && existingDebtors.value.length > 0) {
        const nameMatch = findDebtorByName(aiName)
        if (nameMatch) {
          try {
            const resp = await axios.get(`/debtor/view?id=${nameMatch.value}`)
            if (resp.status === 200 && resp.data.data) {
              const d = resp.data.data
              const dCvr = (d.cvr_number || '').replace(/\s/g, '')
              const dCpr = (d.cpr || '').replace(/[-\s]/g, '')

              const verdict = evaluateDebtorMatch(aiDebtor, d)

              if (verdict) {
                // Tilføj til maps
                if (dCvr) debtorCvrMap[dCvr] = { debtorId: d.id, name: d.name }
                if (dCpr) debtorCprMap[dCpr] = { debtorId: d.id, name: d.name }
                setMatchedExistingDebtor(d.id, {
                  id: d.id,
                  name: d.name,
                  matchType: verdict.matchType,
                  matchStrength: verdict.strength,
                })
                return d.id
              }
            }
          } catch {
            // Ignore
          }
        }
      }

      // Strategi 2: Hvis vi har CVR/CPR men ingen navnmatch, søg i alle dropdown-skyldnere
      // Hent detaljer for op til 10 skyldnere parallelt for hurtig matching
      if ((aiCvr || aiCpr) && existingDebtors.value.length > 0) {
        const batch = existingDebtors.value.slice(0, 10)
        const results = await Promise.all(
          batch.map(async d => {
            try {
              const resp = await axios.get(`/debtor/view?id=${d.value}`, {
                signal: backgroundAbortController.signal,
              })
              return resp.data.data || null
            } catch { return null }
          }),
        )

        for (let i = 0; i < results.length; i += 1) {
          const d = results[i]
          if (!d) {
            // Skip null results
          } else {
            const dCvr = (d.cvr_number || '').replace(/\s/g, '')
            const dCpr = (d.cpr || '').replace(/[-\s]/g, '')
            if (dCvr) debtorCvrMap[dCvr] = { debtorId: d.id, name: d.name }
            if (dCpr) debtorCprMap[dCpr] = { debtorId: d.id, name: d.name }

            // Denne strategi kører kun, når AI har CVR/CPR, og formålet er at
            // finde et entydigt nummermatch. Svage navnematch hører ikke til
            // her — dem fanger navnesøgningen i strategi 1.
            const verdict = evaluateDebtorMatch(aiDebtor, d)
            if (verdict && verdict.strength === 'strong') {
              setMatchedExistingDebtor(d.id, {
                id: d.id,
                name: d.name,
                matchType: verdict.matchType,
                matchStrength: verdict.strength,
              })
              return d.id
            }
          }
        }

        // Bevidst ingen efterfølgende hentning af resten her.
        //
        // Her stod tidligere en forEach over ALLE resterende skyldnere, som
        // affyrede ét request per skyldner på én gang. Med nogle tusind
        // skyldnere løb browseren tør med ERR_INSUFFICIENT_RESOURCES, og
        // alle kald på siden fejlede — inklusive dem matchningen selv
        // afhang af. buildDebtorMapsInBackground dækker behovet, i et
        // tempo browseren kan følge med til.
      }

      return null
    }

    // Eksisterende debtor selection
    async function handleExistingDebtorSelect(debtorId, selectedDebtorId) {
      if (!selectedDebtorId) {
        emit('update-debtor', debtorId, 'isExisting', false)
        emit('update-debtor', debtorId, 'debtorId', null)
        return
      }

      try {
        const response = await axios.get(`/debtor/view?id=${selectedDebtorId}`)
        if (response.status === 200 && response.data.data) {
          const { data } = response.data

          emit('update-debtor', debtorId, 'isExisting', true)
          emit('update-debtor', debtorId, 'debtorId', data.id || selectedDebtorId)
          emit('update-debtor', debtorId, 'name', data.name || '')
          emit('update-debtor', debtorId, 'co', data.care_of || '')
          emit('update-debtor', debtorId, 'debtorType', data.debtor_type || '')
          emit('update-debtor', debtorId, 'cvr', data.cvr_number || '')
          emit('update-debtor', debtorId, 'cpr', data.cpr || '')
          emit('update-debtor', debtorId, 'phone', data.phone || '')
          emit('update-debtor', debtorId, 'email', data.email || '')
          // Adresse ligger under debtor_address i API-responsen
          const addr = data.debtor_address || {}
          emit('update-debtor', debtorId, 'address.roadName', addr.road_name || '')
          emit('update-debtor', debtorId, 'address.houseNumber', addr.house_no || '')
          emit('update-debtor', debtorId, 'address.place', addr.place || '')
          emit('update-debtor', debtorId, 'address.zipCode', addr.zip_code || '')
          emit('update-debtor', debtorId, 'address.city', addr.city || '')
          emit('update-debtor', debtorId, 'address.countryId', addr.country_id || 247)
          // Bankoplysninger
          emit('update-debtor', debtorId, 'bankBranchNo', data.bank_branch_no || '')
          emit('update-debtor', debtorId, 'bankAccountNo', data.bank_account_no || '')
          emit('update-debtor', debtorId, 'cpn', data.contact_person || '')
        }
      } catch (error) {
        console.error('Could not fetch debtor:', error)
      }
    }

    /**
     * Henter eksisterende skyldnere dropdown (hurtigt, ét API-kald).
     */
    async function fetchDropdown() {
      if (!props.clientId) return

      try {
        const response = await axios.get(`/debtor/dropdown?client_id=${props.clientId}`)
        if (response.status === 200 && response.data.data) {
          existingDebtors.value = response.data.data
        }
      } catch (error) {
        console.error('Could not fetch debtors:', error)
      }
    }

    /**
     * Bygger CVR/CPR maps i baggrunden (non-blocking).
     * Bruges til senere manuel CVR/CPR input matching.
     */
    function buildDebtorMapsInBackground() {
      const { signal } = backgroundAbortController

      // Loftet er nødvendigt. Uden det bliver der før eller siden sendt ét
      // request per skyldner i databasen — en klient kan have tusinder — bare
      // for at kunne slå ét CVR-nummer op. Kortet er en bekvemmelighed til
      // manuel indtastning, ikke noget der skal være komplet.
      const debtors = existingDebtors.value.slice(0, MAX_BACKGROUND_DEBTOR_LOOKUPS)

      function runBatch(startIndex) {
        if (signal.aborted || startIndex >= debtors.length) return
        const batch = debtors.slice(startIndex, startIndex + BACKGROUND_LOOKUP_CONCURRENCY)
        Promise.all(batch.map(d => fetchDebtorDetails(d.value, signal)))
          .then(() => runBatch(startIndex + BACKGROUND_LOOKUP_CONCURRENCY))
      }

      runBatch(0)
    }

    // ============================================================
    // AI DATA ACTIONS
    // ============================================================

    /**
     * Bruger den matchede eksisterende skyldner.
     */
    async function useMatchedDebtor() {
      if (!matchedExistingDebtor.value || props.debtors.length === 0) return

      const debtorId = props.debtors[0].id
      await handleExistingDebtorSelect(debtorId, matchedExistingDebtor.value.id)
    }

    /**
     * Anvender AI-udtrukket data som ny skyldner (ingen API-kald, instant).
     */
    /**
     * Henter den rigtige virksomhedsform fra CVR-registret.
     *
     * Kører uden at blokere formularen: felterne udfyldes med det samme, og
     * typen rettes, når registret svarer.
     */
    async function refineDebtorTypeFromCvr(debtorId, cvr) {
      try {
        const result = await cvrLookup(cvr, props.clientId)
        if (result && result.found && result.data && result.data.debtorType) {
          emit('update-debtor', debtorId, 'debtorType', result.data.debtorType)
        }
      } catch (error) {
        // Opslaget er en forbedring, ikke en forudsætning. Fejler det,
        // beholder vi den foreløbige type.
      }
    }

    function applyAiDataToDebtor(debtor, ai) {
      let determinedType = ''
      if (ai.cvr) {
        // Foreløbig antagelse. Et CVR-nummer siger INTET om selskabsform —
        // enkeltmandsvirksomheder har også CVR, og de blev derfor alle
        // fejlagtigt sat til kapitalselskab. Registret afgør det nedenfor.
        determinedType = 'capital-company'
        emit('update-debtor', debtor.id, 'cvr', ai.cvr)
        refineDebtorTypeFromCvr(debtor.id, ai.cvr)
      } else if (ai.cpr) {
        determinedType = 'person'
        emit('update-debtor', debtor.id, 'cpr', ai.cpr)
      } else if (ai.companyName) {
        determinedType = 'capital-company'
      } else if (ai.personName) {
        determinedType = 'person'
      } else {
        determinedType = 'capital-company'
      }

      emit('update-debtor', debtor.id, 'debtorType', determinedType)
      emit('update-debtor', debtor.id, 'name', ai.companyName || ai.personName || '')
      emit('update-debtor', debtor.id, 'address.roadName', ai.address || '')
      emit('update-debtor', debtor.id, 'address.houseNumber', ai.houseNumber || '')
      emit('update-debtor', debtor.id, 'address.zipCode', ai.zipCode || '')
      emit('update-debtor', debtor.id, 'address.city', ai.city || '')
      emit('update-debtor', debtor.id, 'phone', ai.phone || '')
      emit('update-debtor', debtor.id, 'email', ai.email || '')
      emit('update-debtor', debtor.id, 'aiPrefilled', true)
    }

    /**
     * Bruger AI-udtrukket data - enten som eksisterende skyldner eller ny.
     * Anvender AI-data STRAKS, derefter matcher mod eksisterende.
     */
    async function useAiExtractedDebtor() {
      if (!aiExtractedDebtor.value || props.debtors.length === 0) return

      const debtor = props.debtors[0]
      const ai = aiExtractedDebtor.value

      // Anvend AI-data med det samme (instant, ingen API-kald)
      applyAiDataToDebtor(debtor, ai)
    }

    // Debtor type helpers
    function getDebtorTypeIcon(type) {
      switch (type) {
        case 'person': return 'UserIcon'
        case 'sole-proprietorship': return 'BriefcaseIcon'
        // Feather har intet "Building"-ikon, så kortet stod helt uden ikon.
        // UsersIcon findes og læses som en juridisk enhed med flere bag sig.
        case 'capital-company': return 'UsersIcon'
        default: return 'UserIcon'
      }
    }

    function getDebtorTypeHint(type) {
      switch (type) {
        case 'person': return root.$t('DEBTIA_CREATE_DEBTOR_HINT_PERSON')
        case 'sole-proprietorship': return root.$t('DEBTIA_CREATE_DEBTOR_HINT_SOLE')
        case 'capital-company': return root.$t('DEBTIA_CREATE_DEBTOR_HINT_COMPANY')
        default: return ''
      }
    }

    function getDebtorTypeLabelDa(type) {
      const found = props.debtorTypes.find(t => t.value === type)
      return found ? found.labelDa : type
    }

    function toggleDebtorTypeSelector(debtorId) {
      // vueSet er nødvendig: Vue 2 kan ikke opdage, at en NY nøgle tilføjes til
      // et reactive-objekt. Nøglen findes ikke, før der klikkes første gang, så
      // en almindelig tildeling satte værdien uden at udløse gentegning —
      // knappen så død ud.
      vueSet(showDebtorTypeSelector, debtorId, !showDebtorTypeSelector[debtorId])
    }

    function handleDebtorTypeChange(debtorId, newType) {
      emit('update-debtor', debtorId, 'debtorType', newType)
      emit('update-debtor', debtorId, 'cvr', '')
      emit('update-debtor', debtorId, 'cpr', '')
      delete cvrMessage[debtorId]
      delete cvrMessageSuccess[debtorId]
      // Skjul type selector igen efter valg
      vueSet(showDebtorTypeSelector, debtorId, false)
    }

    // CVR validation and lookup
    function cvrValidationState(debtor) {
      if (!debtor.cvr) return null
      return debtor.cvr.length === 8
    }

    function cvrMessageClass(debtorId) {
      if (!cvrMessage[debtorId]) return ''
      return cvrMessageSuccess[debtorId] ? 'text-success' : 'text-danger'
    }

    async function lookupCvr(debtor) {
      if (!debtor.cvr || debtor.cvr.length !== 8) return

      cvrLoading[debtor.id] = true
      cvrMessage[debtor.id] = ''
      cvrMessageSuccess[debtor.id] = false

      try {
        // Tjek først lokal CVR map - eksisterende skyldner?
        const localMatch = findDebtorByCvrLocal(debtor.cvr)
        if (localMatch) {
          // Auto-vælg eksisterende skyldner og udfyld alle felter
          await handleExistingDebtorSelect(debtor.id, localMatch.debtorId)

          // Hent også CVR-data for evt. adresseopdatering
          const externalResult = await cvrLookup(debtor.cvr, props.clientId)
          if (externalResult.found && externalResult.data?.address) {
            // Opdater adresse hvis den er anderledes (CVR-register er autoritativt)
            const addr = externalResult.data.address
            if (addr.roadName) emit('update-debtor', debtor.id, 'address.roadName', addr.roadName)
            if (addr.houseNumber) emit('update-debtor', debtor.id, 'address.houseNumber', addr.houseNumber)
            if (addr.zipCode) emit('update-debtor', debtor.id, 'address.zipCode', addr.zipCode)
            if (addr.city) emit('update-debtor', debtor.id, 'address.city', addr.city)
          }

          cvrMessage[debtor.id] = root.$t('DEBTIA_CREATE_DEBTOR_EXISTING_DEBTOR', { name: localMatch.name })
          cvrMessageSuccess[debtor.id] = true
          return
        }

        // Ikke fundet lokalt - brug cvrService (intern DB + eksternt CVR-register)
        const result = await cvrLookup(debtor.cvr, props.clientId)

        if (result.found && result.data) {
          if (result.isExistingDebtor) {
            // Fundet i intern DB - auto-vælg eksisterende
            await handleExistingDebtorSelect(debtor.id, result.data.debtorId)
            cvrMessage[debtor.id] = root.$t('DEBTIA_CREATE_DEBTOR_EXISTING_DEBTOR', { name: result.data.name })
            cvrMessageSuccess[debtor.id] = true
          } else {
            // Fundet i eksternt CVR-register - udfyld data
            emit('update-debtor', debtor.id, 'name', result.data.name || '')
            // Registrets virksomhedsform er den autoritative kilde til typen.
            // Den blev hentet, men aldrig anvendt.
            if (result.data.debtorType) {
              emit('update-debtor', debtor.id, 'debtorType', result.data.debtorType)
            }
            if (result.data.address) {
              emit('update-debtor', debtor.id, 'address.roadName', result.data.address.roadName || '')
              emit('update-debtor', debtor.id, 'address.houseNumber', result.data.address.houseNumber || '')
              emit('update-debtor', debtor.id, 'address.zipCode', result.data.address.zipCode || '')
              emit('update-debtor', debtor.id, 'address.city', result.data.address.city || '')
            }
            cvrMessage[debtor.id] = result.message
            cvrMessageSuccess[debtor.id] = true
          }
        } else {
          cvrMessage[debtor.id] = result.message || root.$t('DEBTIA_CREATE_DEBTOR_CVR_NOT_FOUND')
          cvrMessageSuccess[debtor.id] = false
        }
      } catch (error) {
        cvrMessage[debtor.id] = root.$t('DEBTIA_CREATE_DEBTOR_CVR_LOOKUP_ERROR')
        cvrMessageSuccess[debtor.id] = false
      } finally {
        cvrLoading[debtor.id] = false
      }
    }

    function handleCvrBlur(debtor) {
      if (debtor.cvr && debtor.cvr.length === 8) {
        lookupCvr(debtor)
      }
    }

    /**
     * Håndterer CPR blur - tjekker om CPR allerede findes blandt eksisterende skyldnere.
     */
    async function handleCprBlur(debtor, cprValue) {
      emit('update-debtor', debtor.id, 'cpr', cprValue)
      if (!cprValue || cprValue.replace(/[-\s]/g, '').length < 6) return

      const cprMatch = findDebtorByCprLocal(cprValue)
      if (cprMatch) {
        await handleExistingDebtorSelect(debtor.id, cprMatch.debtorId)
        cvrMessage[debtor.id] = root.$t('DEBTIA_CREATE_DEBTOR_EXISTING_DEBTOR', { name: cprMatch.name })
        cvrMessageSuccess[debtor.id] = true
      }
    }

    // Zip code lookup
    async function handleZipCodeChange(debtor, zipCode) {
      emit('update-debtor', debtor.id, 'address.zipCode', zipCode)

      if (zipCode && zipCode.length === 4) {
        cityLoading[debtor.id] = true
        try {
          const response = await axios.get(`${constants.debtiaappUrl}/settings/get-city-name-by-code?code=${zipCode}`)
          if (response.data.data && response.data.data.city_name) {
            emit('update-debtor', debtor.id, 'address.city', response.data.data.city_name)
          }
        } catch {
          // Ignore
        } finally {
          cityLoading[debtor.id] = false
        }
      }
    }

    // Lifecycle - optimeret for hastighed
    onMounted(async () => {
      const firstDebtor = props.debtors[0]
      const hasAiData = state.aiExtractedDebtor && firstDebtor && !firstDebtor.name && !firstDebtor.aiPrefilled

      // 1. Anvend AI-data STRAKS (instant, ingen API-kald)
      if (hasAiData) {
        applyAiDataToDebtor(firstDebtor, state.aiExtractedDebtor)
      }

      // 2. Hent dropdown (ét hurtigt API-kald)
      await fetchDropdown()

      // 3. Match AI-skyldner mod eksisterende (1-2 API-kald max)
      if (hasAiData && existingDebtors.value.length > 0) {
        const matchedId = await quickMatchAiDebtor()
        if (matchedId) {
          // Eksisterende skyldner fundet - erstat AI-data med eksisterende
          await handleExistingDebtorSelect(firstDebtor.id, matchedId)
          const matched = state.matchedExistingDebtor
          if (matched) {
            cvrMessage[firstDebtor.id] = root.$t('DEBTIA_CREATE_DEBTOR_EXISTING_DEBTOR', { name: matched.name })
            cvrMessageSuccess[firstDebtor.id] = true
          }
        }
      }

      // 4. Byg CVR/CPR maps i baggrunden (non-blocking, til manual input)
      buildDebtorMapsInBackground()
    })

    // Annuller alle baggrunds-requests ved navigation væk fra dette step
    onBeforeUnmount(() => {
      backgroundAbortController.abort()
    })

    // Watch for AI debtor changes (når AI finder data efter mount)
    watch(() => state.aiExtractedDebtor, async () => {
      if (!state.aiExtractedDebtor) return
      const firstDebtor = props.debtors[0]
      if (!firstDebtor || firstDebtor.aiPrefilled) return

      // Anvend AI-data straks
      applyAiDataToDebtor(firstDebtor, state.aiExtractedDebtor)

      // Match mod eksisterende
      if (existingDebtors.value.length > 0) {
        const matchedId = await quickMatchAiDebtor()
        if (matchedId) {
          await handleExistingDebtorSelect(firstDebtor.id, matchedId)
          const matched = state.matchedExistingDebtor
          if (matched) {
            cvrMessage[firstDebtor.id] = root.$t('DEBTIA_CREATE_DEBTOR_EXISTING_DEBTOR', { name: matched.name })
            cvrMessageSuccess[firstDebtor.id] = true
          }
        }
      }
    })

    return {
      existingDebtors,
      aiExtractedDebtor,
      matchedExistingDebtor,
      cvrLoading,
      cityLoading,
      cvrMessage,
      showDebtorTypeSelector,
      useMatchedDebtor,
      useAiExtractedDebtor,
      handleExistingDebtorSelect,
      getDebtorTypeIcon,
      getDebtorTypeHint,
      getDebtorTypeLabelDa,
      toggleDebtorTypeSelector,
      handleDebtorTypeChange,
      cvrValidationState,
      cvrMessageClass,
      lookupCvr,
      handleCvrBlur,
      handleCprBlur,
      handleZipCodeChange,
      step2ValidationErrors,
    }
  },
}
</script>

<style lang="scss" scoped>
// AI Match Banner
.ai-match-banner {
  background: rgba(115, 103, 240, 0.05);
  border: 1px solid rgba(115, 103, 240, 0.2);
  border-radius: 0.5rem;
  overflow: hidden;

  .match-header {
    padding: 0.75rem 1rem;
    background: rgba(115, 103, 240, 0.1);
    border-bottom: 1px solid rgba(115, 103, 240, 0.2);
    display: flex;
    align-items: center;
  }

  .match-content {
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .match-found {
    background: rgba(40, 199, 111, 0.05);
  }
}

// Debtor card
.debtor-card {
  margin-bottom: 1.5rem;

  .card {
    border: 1px solid #e9ecef;
  }
}

.debtor-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

// Debtor type selector
.debtor-type-selector {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.debtor-type-option {
  flex: 1;
  padding: 1.25rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  text-align: center;

  &:hover {
    border-color: #7367f0;
    background: rgba(115, 103, 240, 0.05);
  }

  &.selected {
    border-color: #7367f0;
    background: rgba(115, 103, 240, 0.1);
  }

  .option-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .option-label {
    font-weight: 700;
    color: #5e5873;
    font-size: 1rem;
  }

  .option-hint {
    font-size: 0.8rem;
    color: #82868b;
    margin-top: 0.35rem;
    line-height: 1.3;
  }

  .option-check {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    color: #7367f0;
  }
}

// Valgt skyldnertype visning (når selector er skjult)
.selected-debtor-type {
  padding: 0.5rem 0.75rem;
  background: rgba(115, 103, 240, 0.08);
  border-radius: 0.375rem;
  display: inline-flex;
}

// Validation Alert
.validation-alert {
  padding: 1rem 1.25rem;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-left: 4px solid #ff9f43;
  border-radius: 0.5rem;

  ul {
    list-style: none;
    padding-left: 0;

    li {
      padding: 0.25rem 0;
      font-size: 0.9rem;

      &::before {
        content: '\2022';
        color: #ff9f43;
        font-weight: 700;
        display: inline-block;
        width: 1em;
      }
    }
  }
}

@media (max-width: 576px) {
  .debtor-type-selector {
    flex-direction: column;
  }
}

// ---- Dark Mode ----
.dark-layout {
  .debtor-card .card {
    border-color: #163350;
  }

  .debtor-header {
    border-color: #163350;
  }

  .debtor-type-option {
    border-color: #163350;
    background: #091828;

    &:hover {
      border-color: #7367f0;
      background: rgba(115, 103, 240, 0.08);
    }

    &.selected {
      border-color: #7367f0;
      background: rgba(115, 103, 240, 0.12);
    }

    .option-label {
      color: #e2e8f0;
    }

    .option-hint {
      color: #6b8299;
    }
  }

  .selected-debtor-type {
    background: rgba(115, 103, 240, 0.1);
  }

  .match-found {
    background: rgba(40, 199, 111, 0.08);
  }

  .validation-alert {
    background: rgba(255, 159, 67, 0.08);
    border-color: rgba(255, 159, 67, 0.2);
    border-left-color: #ff9f43;

    strong {
      color: #e2e8f0;
    }

    li {
      color: #c5cdd8;
    }
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.existing-debtor-select {
  width: 100%;
}
</style>
